/**
 * One claim's reports, as the Lab's evidence genealogy counts them.
 *
 * The Research Lab's Evidence tab prints "N records · M independence units" over
 * the whole exploration, and a usability study's epidemiologist found that count
 * — how many studies are really behind the sources — the most useful thing in
 * the app, eight tabs away from the claim it was about. This module brings the
 * same count to one claim, and it is the SAME count: the projection is the
 * Lab's own (`projectSessionEvidence`, which is what `EvidencePanel` renders),
 * the families are that projection's genealogy, and the tally is
 * `independenceTally` in `genealogy.ts`. Nothing here decides what is
 * independent; it only asks the genealogy about one claim's records.
 *
 * A claim's records are what the projection BINDS to it and marks current: the
 * sources and cited passages of its live source check, and any artefact bound
 * to it in the Lab. A check that read an earlier wording is not current — a
 * check of other words is not a check of these — so it contributes nothing.
 *
 * It reaches `researchProjection.ts`, which reaches neither the store nor the
 * coach, so `cues.ts` can read it without joining the boot cycle the store's
 * imports have to stay out of.
 */

import { independenceTally, type IndependenceTally } from './genealogy'
import { projectSessionEvidence, type SessionEvidenceProjection } from './researchProjection'
import { verificationLive, type ReasonNode, type Session, type SourceDesign } from './types'
import { evidenceKinds } from './vocabulary'

/**
 * The Lab's projection, built once per state of the evidence.
 *
 * A document is replaced, never mutated, on every write, so its identity is a
 * complete cache key — but too fine a one here. The genealogy reads only the
 * checks, the recorded outcomes and the research record, and a document with a
 * large evidence record took seconds to build it (5.8 s on the durability
 * fixture): keyed on the document, every edit anywhere — a rewrite, a fold, a
 * pass landing elsewhere — rebuilt it, and the coach's resting cue and the card
 * each asked on the first render after boot. So the last build is kept with the
 * inputs it read and reused while those are the same objects, compared shallowly
 * in one walk of the order, which costs nothing beside the build it saves.
 */
interface Built {
  research: Session['research']
  inputs: unknown[]
  projection: SessionEvidenceProjection
}
let last: Built | null = null

function evidenceInputs(session: Session): unknown[] {
  const out: unknown[] = []
  for (const id of session.order) {
    const n = session.nodes[id]
    if (!n) continue
    // The check itself, its history and its liveness (which reads the claim's
    // wording), and the outcome with the label its record falls back to:
    // everything `projectSessionEvidence` reads off a node. Strings compare by
    // value, which is what liveness and the label read.
    out.push(
      id,
      n.verification,
      n.verificationHistory,
      n.verification ? n.claim : null,
      n.outcome,
      n.outcome ? n.label : null,
    )
  }
  return out
}

export function sessionEvidence(session: Session): SessionEvidenceProjection {
  const inputs = evidenceInputs(session)
  if (
    last &&
    last.research === session.research &&
    last.inputs.length === inputs.length &&
    last.inputs.every((v, i) => v === inputs[i])
  )
    return last.projection
  const projection = projectSessionEvidence(session)
  last = { research: session.research, inputs, projection }
  return projection
}

/**
 * At most how many reports the projection could bind to this claim as current,
 * read without building it. A claim's current records are its live check's
 * sources and whatever the Lab binds to it by hand (`projectVerification`,
 * `projectManualBindings`), so fewer than two here means no count to print and no
 * group to suggest — and the genealogy is never built for a claim that cannot
 * have one, which on most claims of most documents is every claim.
 */
export function reportBound(session: Session, nodeId: string): number {
  const node = session.nodes[nodeId]
  if (!node) return 0
  let n = node.verification && verificationLive(node) ? node.verification.sources.length : 0
  for (const b of session.research?.manualEvidenceBindings ?? []) if (b.nodeId === nodeId) n++
  return n
}

/** The records bound to this claim and current, in first-seen order. */
export function claimRecordIds(evidence: SessionEvidenceProjection, nodeId: string): string[] {
  const out: string[] = []
  const seen = new Set<string>()
  for (const row of evidence.bindings) {
    if (row.nodeId !== nodeId || !row.current || seen.has(row.recordId)) continue
    seen.add(row.recordId)
    out.push(row.recordId)
  }
  return out
}

/** This claim's reports and the independence units they come to. */
export function claimTally(session: Session, nodeId: string): IndependenceTally {
  const evidence = sessionEvidence(session)
  return independenceTally(evidence.genealogy, claimRecordIds(evidence, nodeId))
}

/**
 * What kind of evidence this claim's live check says its sources are, counted
 * (`WebSource.design`). Only the check that still describes the claim's words: a
 * check of an earlier wording is not a check of these, the rule `claimRecordIds`
 * applies through the projection's `current`. An annotation read by the card's
 * one clause (`evidenceKinds`) and by nothing that gates, orders or colours.
 */
export function designTally(node: ReasonNode): Map<SourceDesign, number> {
  const kinds = new Map<SourceDesign, number>()
  if (!node.verification || !verificationLive(node)) return kinds
  for (const s of node.verification.sources) {
    if (s.design) kinds.set(s.design, (kinds.get(s.design) ?? 0) + 1)
  }
  return kinds
}

/**
 * This claim's reports and their independence units, where there is a count to
 * print: past one report (`reportBound`), and never on the seed, whose card
 * shows its survey instead. Undefined otherwise — a single source is one unit by
 * definition, and the genealogy is not built for a claim that cannot have two.
 * The card's guard, lifted out so the report asks the same question.
 */
export function claimReports(session: Session, nodeId: string): IndependenceTally | undefined {
  const node = session.nodes[nodeId]
  if (!node || node.parentId === null || reportBound(session, nodeId) < 2) return undefined
  return claimTally(session, nodeId)
}

/**
 * The card's clause, whole — "5 reports · 1 independent: 1 observational study,
 * 4 news reports", or "1 trial" — for a surface that prints it without the card:
 * the research report and its PDF. The same two readings the card makes
 * (`claimReports`, `designTally`) through the same words (`evidenceKinds`), so
 * the card and the document cannot count one claim two ways. Null where the card
 * says nothing. An annotation: nothing that gates or reads a check reads this.
 */
export function evidenceClause(session: Session, nodeId: string): string | null {
  const node = session.nodes[nodeId]
  if (!node || node.parentId === null) return null
  return evidenceKinds(claimReports(session, nodeId), designTally(node))
}
